<script setup>
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useFetch } from "@/api/fetch.js";
import env from "@/env.js";
import { redirectToAdminApp } from "@/utils.js";

const route = useRoute();
const platform = ["console", "reseller"].includes(route.query.target) ? route.query.target : "";
const accountScan = platform === "reseller" && route.query.purpose === "add-account";
const router = useRouter();
const { postJson, getJson } = useFetch();

const loading = ref(true);
const info = ref("正在处理，请稍候...");
const chains = ref([]);
const shopTypeNameMap = ref({});
const enterpriseChainType = ref({});

const loadShopTypes = async () => {
  try {
    const data = await getJson("config/shopTypes");
    if (data?.info !== "OK") return;
    shopTypeNameMap.value = Object.fromEntries(
      (data.shopTypes || []).map((shopType) => [shopType.type, shopType.name]),
    );
  } catch (error) {
    console.error("读取门店类型失败:", error);
  }
};

const loadEnterpriseChainType = async () => {
  try {
    const data = await getJson("config/enterpriseChainType");
    if (data?.info !== "OK") return;
    enterpriseChainType.value = data.enterpriseChainType || {};
  } catch (error) {
    console.error("读取行业专业版产品线配置失败:", error);
  }
};

const getShopTypeName = (chain = {}) => {
  const type = chain.type || chain.chain?.type;
  return (
    enterpriseChainType.value[type]?.name ||
    shopTypeNameMap.value[type] ||
    chain.productName ||
    type ||
    ""
  );
};

const getLoginTargetName = (chain = {}) => {
  return chain.displayName || chain.shop?.name || chain.name || "未命名商户";
};

const getAppName = (chain = {}) => {
  const type = chain.type || chain.chain?.type;
  if (enterpriseChainType.value[type]) return "专业版";
  return "标准版";
};

const loginChain = async (chainId) => {
  loading.value = true;
  info.value = "正在登录，请稍候...";

  const res = await postJson("wxScan", "loginChain", {
    chainId,
  });

  if (res?.info === "OK") {
    info.value = "登录成功，正在进入系统...";
    redirectToAdminApp(res);
  } else {
    info.value = res?.info || res?.error || "登录失败，请重试";
    loading.value = false;
  }
};

const handleCallback = async () => {
  try {
    const code = route.query.code;
    const state = route.query.state;
    if (!code) throw new Error("未获取到微信授权码");

    if (accountScan) {
      const session = await postJson('wxScan', 'getSessionInfo', { sessionInfo: 'reseller' });
      if (session.info !== 'OK') throw new Error('管理员登录已失效，请重新登录');
      const result = await postJson('wxScan', 'getStaffChains_v2', { code, state, sessionInfo: 'reseller', purpose: 'add-account', csrfToken: session.csrfToken });
      if (result.info !== 'OK') throw new Error(result.error || '扫码识别失败，请重新扫码');
      info.value = '已识别，请在添加弹窗确认人员';
      window.parent.postMessage({ type: 'reseller-account-identified', scanToken: result.scanToken, person: result.person }, env.host);
      return;
    }
    if (platform) {
      const result = await postJson("wxScan", "getStaffChains_v2", { code, state, sessionInfo: platform });
      if (result.info !== "OK") throw new Error(result.error || "代理商登录失败");
      redirectToAdminApp(result);
      return;
    }

    const res = await postJson("wxScan", "getStaffChains_v2", {
      code,
      state,
      app: "login",
    });

    if (res?.info !== "OK") {
      info.value = res?.info || res?.error || "没有查到您有权限登录的门店";
      return;
    }

    if ((res.chains || []).length === 1) {
      info.value = "正在登录...";
      await loginChain(res.chains[0]._id);
      return;
    }

    chains.value = res.chains || [];
  } catch (error) {
    info.value = error?.message || "登录失败，请稍后重试";
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  if (!platform) await Promise.all([loadShopTypes(), loadEnterpriseChainType()]);
  await handleCallback();
});
</script>

<template>
  <v-container class="callback-page" :class="{ 'account-scan-callback': accountScan }">
    <div v-if="loading" class="state-box">
      <v-progress-circular indeterminate color="primary" />
      <p class="mt-4 text-grey-darken-1">{{ info }}</p>
    </div>

    <v-card v-else-if="chains.length > 0" title="请选择门店登录" variant="outlined" class="select-panel">
      <v-card-text>
        <v-card
          v-for="chain in chains"
          :key="chain._id"
          class="chain-card"
          variant="outlined"
          @click="loginChain(chain._id)"
        >
          <v-card-title>{{ getLoginTargetName(chain) }}</v-card-title>
          <v-card-subtitle>
            <span v-if="getShopTypeName(chain)">{{ getShopTypeName(chain) }}</span>
            <span v-if="getShopTypeName(chain)" class="mx-1">｜</span>
            <span>{{ getAppName(chain) }}</span>
          </v-card-subtitle>
          <v-card-actions>
            <v-spacer />
            <v-btn color="primary" variant="text">进入</v-btn>
          </v-card-actions>
        </v-card>
      </v-card-text>
    </v-card>

    <v-card v-else class="select-panel" variant="outlined">
      <v-card-text>{{ info }}</v-card-text>
      <v-card-actions v-if="!accountScan">
        <v-spacer />
        <v-btn color="primary" @click="router.push({ name: '统一登录', query: platform ? { target: platform } : {} })">返回登录</v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<style scoped>
.callback-page.account-scan-callback { padding: 16px; }
.account-scan-callback .state-box { min-height: 180px; }
.callback-page {
  min-height: 100vh;
  max-width: 640px;
  padding-top: 80px;
}

.state-box {
  min-height: 360px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.select-panel,
.chain-card {
  border-radius: 8px;
}

.chain-card {
  cursor: pointer;
  margin: 12px 0;
}
</style>
